import {
  LightButton,
  LoadingLayout,
  PageLayout,
  PlainButton,
  Separator,
  Text,
} from '../../../components';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { E_Route } from '@repo-sapiens/constants';
import { config } from '../../../config';
import { css, keyframes } from '@emotion/css';
import {
  MultiplayerWhitePlainLogo,
  QuoteIcon,
  StarIcon,
} from '../../../components/icons';
import { PricingOption, PricingTabs } from '../components';
import { EActivityEvent } from '@repo-sapiens/data-access-models';
import { usePageViewed, useSubscriptionPlan } from '../../../hooks';
import axios from 'axios';
import { format } from 'date-fns';
import { UserContext } from '../../../context';

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInFromRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  25% {
    transform: translateY(-10px) rotate(-2deg);
  }
  75% {
    transform: translateY(5px) rotate(2deg);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const drawLine = keyframes`
  from {
    height: 0;
  }
  to {
    height: 100%;
  }
`;

const drawLineHorizontal = keyframes`
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
`;

const drawLineGradient = keyframes`
  from {
    height: 0;
    opacity: 0;
  }
  to {
    height: 105%;
    opacity: 1;
  }
`;

const bounceIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
  }
`;

const date = new Date();
date.setDate(date.getDate() + 7);

const formattedDate = format(date, 'MMMM dd, yyyy');

const quantity = {
  monthly: 1,
  quarterly: 3,
  yearly: 12,
};

const pricingTexts = [
  'Organize and archive emails into categories',
  'Suggests meeting times from your Google Calendar',
  'Join meetings and summarizes notes and next steps',
  'Meeting prep',
  'Daily podcast',
  'Action items (coming soon)',
];

export const ChoosePlanScreen: React.FC = () => {
  const [pricingOption, setPricingOption] =
    React.useState<PricingOption>('quarterly');
  const [normalPrice, setNormalPrice] = useState<number>(0);
  const [annualDiscountPrice, setAnnualDiscountPrice] = useState<number>(0);
  const [quarterlyDiscountPrice, setQuarterlyDiscountPrice] =
    useState<number>(0);
  const [quoteVisible, setQuoteVisible] = useState(false);
  const [displayedQuote, setDisplayedQuote] = useState('');
  const fullQuote =
    'Amazing!! I love this platform. If you are an entrepreneur, this is for you!';

  const { state: userState } = useContext(UserContext);

  usePageViewed(EActivityEvent.BillingPageViewed);

  const {
    loading,
    vipUser,
    stripeCustomerId,
    isTrial,
    isCanceled,
    ...subscription
  } = useSubscriptionPlan(1000);

  useEffect(() => {
    axios
      .get(`${config.apiUrl}/billing/get-credit-cost`)
      .then((res) => {
        setNormalPrice((res.data?.normalPrice || 0) / 100);
        setQuarterlyDiscountPrice(
          (res.data?.quarterlyDiscountPrice || 0) / 100,
        );
        setAnnualDiscountPrice((res.data?.annualDiscountPrice || 0) / 100);
      })
      .catch((err) => {
        console.log('err =====> ', err);
      });
  }, []);

  const selectedPrice = useMemo(() => {
    switch (pricingOption) {
      case 'yearly':
        return annualDiscountPrice / 12;
      case 'quarterly':
        return quarterlyDiscountPrice / 3;
      default:
        return normalPrice;
    }
  }, [pricingOption, normalPrice, annualDiscountPrice, quarterlyDiscountPrice]);

  const handleSelectPricingOption = (option: PricingOption) => {
    setPricingOption(option);
  };

  const navigate = useNavigate();

  const handleSkip = useCallback(() => {
    navigate(E_Route.Home);
  }, [navigate]);

  useEffect(() => {
    if (
      stripeCustomerId &&
      subscription?.subscription?.plan === 'pro' &&
      !vipUser &&
      !isTrial &&
      !isCanceled
    ) {
      // If the user is a pro user, redirect to home
      navigate(E_Route.Home);
    }
  }, [vipUser, navigate, stripeCustomerId, subscription, isTrial, isCanceled]);

  // Typewriter effect for quote
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuoteVisible(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (quoteVisible) {
      let index = 0;
      const typewriterInterval = setInterval(() => {
        if (index <= fullQuote.length) {
          setDisplayedQuote(fullQuote.slice(0, index));
          index++;
        } else {
          clearInterval(typewriterInterval);
        }
      }, 25);

      return () => clearInterval(typewriterInterval);
    }
  }, [quoteVisible, fullQuote]);

  if (loading || !subscription?.subscription) {
    return <LoadingLayout />;
  }

  return (
    <PageLayout title="Start your free trial | Multiplayer">
      {/* Animated background particles */}
      <div
        className={css`
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: 0;
          overflow: hidden;

          &::before,
          &::after {
            content: '';
            position: absolute;
            width: 500px;
            height: 500px;
            border-radius: 50%;
            opacity: 0.02;
          }

          &::before {
            background: linear-gradient(135deg, #6a53fe, #aaef65);
            top: -250px;
            right: -250px;
            animation: ${float} 25s ease-in-out infinite;
          }

          &::after {
            background: linear-gradient(135deg, #aaef65, #6a53fe);
            bottom: -250px;
            left: -250px;
            animation: ${float} 30s ease-in-out infinite reverse;
          }
        `}
      />

      <div
        className={css`
          position: absolute;
          width: 100%;
          height: 100vh;
          left: 0;
          top: 0;
          display: grid;
          grid-template-columns: 68% 32%;
          gap: 32px;
          align-items: flex-start;
          z-index: 1;
          @media (max-width: 1024px) {
            grid-template-columns: 1fr;
            padding: 12px;
          }
          padding-inline: 24px;
          padding-left: 64px;
          @media (max-width: 648px) {
            padding-left: 12px;
          }
        `}
      >
        <div
          className={css`
            width: 100%;
            padding-top: 64px;
          `}
        >
          <div
            className={css`
              width: 100%;
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: flex-start;
              margin-bottom: 32px;
              animation: ${fadeInUp} 0.8s ease-out;
              @media (max-width: 648px) {
                flex-direction: column;
              }
            `}
          >
            <div
              className={css`
                animation: ${float} 4s ease-in-out;
                height: 72px !important;
                width: 72px !important;
                margin-right: 16px;
                @media (max-width: 648px) {
                  margin-bottom: 16px;
                  margin-right: 0px;
                }
              `}
            >
              <MultiplayerWhitePlainLogo
                style={{ width: '72px', height: '72px' }}
              />
            </div>
            <div
              className={css`
                margin-top: -16px;
                animation: ${slideInFromLeft} 1s ease-out 0.3s both;
                @media (max-width: 648px) {
                  text-align: center;
                  max-width: 300px;
                }
              `}
            >
              {isCanceled ? (
                <Text.H2
                  className={css`
                    background: linear-gradient(
                      90deg,
                      #ffffff,
                      #aaef65,
                      #ffffff
                    );
                    background-size: 200% 100%;
                    -webkit-background-clip: text;
                    background-clip: text;
                    animation: ${gradientShift} 3s ease-in-out infinite;
                  `}
                >
                  Welcome back!
                </Text.H2>
              ) : (
                <Text.H2
                  className={css`
                    background: linear-gradient(
                      90deg,
                      #ffffff,
                      #6a53fe,
                      #ffffff
                    );
                    background-size: 200% 100%;
                    -webkit-background-clip: text;
                    background-clip: text;
                    animation: ${gradientShift} 3s ease-in-out infinite;
                  `}
                >
                  Start your free trial
                </Text.H2>
              )}
              <Text.BodySM
                color="seconday"
                className={css`
                  animation: ${fadeIn} 1.2s ease-out 0.5s both;
                `}
              >
                {isCanceled ? 'Choose your plan: ' : 'First 7 days free, then'}{' '}
                <span
                  style={{
                    fontWeight: 'bold',
                  }}
                  className={css`
                    color: #aaef65;
                    animation: ${pulse} 2s ease-in-out infinite;
                    display: inline-block;
                  `}
                >
                  ${selectedPrice}
                </span>{' '}
                / month
              </Text.BodySM>
            </div>
          </div>

          {/* Workspace Section */}
          <div
            className={css`
              display: flex;
              width: 80%;
              flex-direction: column;
              justify-content: flex-start;
              align-items: flex-start;
              margin-bottom: 32px;
              animation: ${fadeInUp} 0.8s ease-out 0.4s both;
              @media (max-width: 648px) {
                flex-direction: column;
                width: 100%;
              }
            `}
          >
            <div
              className={css`
                animation: ${bounceIn} 0.8s ease-out 0.6s both;
                width: 100%;
                & > * {
                  transition: all 0.3s ease;
                  &:hover {
                    transform: translateY(-2px);
                  }
                }
              `}
            >
              <PricingTabs
                activeTab={pricingOption}
                onSelect={handleSelectPricingOption}
              />
            </div>

            {!isCanceled && (
              <div
                className={css`
                  width: 100%;
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                  margin-top: 62px;
                  margin-bottom: 32px;
                  position: relative;
                  animation: ${fadeInUp} 0.8s ease-out 0.8s both;
                `}
              >
                <div
                  className={css`
                    position: absolute;
                    top: -8px;
                    left: 0px;
                    width: 24px;
                    height: 0;
                    z-index: 0;
                    border-radius: 37.5px;
                    border: 0.313px solid #3b3b3b;
                    background: linear-gradient(
                      180deg,
                      #6a53fe 0%,
                      #aaef65 100%
                    );
                    box-shadow: 0px 2.5px 2.5px 0px rgba(0, 0, 0, 0.25);
                    animation: ${drawLineGradient} 1.5s ease-out 1s forwards;
                    &::after {
                      content: '';
                      position: absolute;
                      width: 100%;
                      height: 100%;
                      border-radius: 37.5px;
                      background: linear-gradient(
                        180deg,
                        rgba(106, 83, 254, 0.3),
                        rgba(170, 239, 101, 0.3)
                      );
                    }
                  `}
                />
                <StepItem
                  title="Today"
                  subtitle="Unlock access to your executive assistant that handles incoming email, draft replies, and meeting follow ups."
                  delay={1.2}
                  isFirst
                />
                <Separator.Horizontal spacing={4} />
                <StepItem
                  title="In 5 days"
                  subtitle="Get a reminder that your trial is ending soon."
                  delay={1.5}
                />
                <Separator.Horizontal spacing={4} />
                <StepItem
                  title="In 7 days"
                  subtitle={`You'll be charged on ${formattedDate}, cancel anytime before.`}
                  delay={1.8}
                  isLast
                />
              </div>
            )}

            {isCanceled && <PricingItem price={selectedPrice} />}

            <Separator.Horizontal spacing={3} />
            <form
              action={`${config.apiUrl}/billing/create-checkout-session`}
              method="POST"
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
              }}
              className={css`
                animation: ${fadeInUp} 0.8s ease-out 2s both;
              `}
            >
              <input type="hidden" name="userId" value={userState?._id} />
              <input type="hidden" name="redirect" value={E_Route.Home} />
              <input
                type="hidden"
                name="failureRedirect"
                value={E_Route.ChoosePlan}
              />
              <input
                type="hidden"
                name="quantity"
                value={quantity[pricingOption]}
              />

              <div
                className={css`
                  width: 90%;
                  position: relative;
                  & button {
                    position: relative;
                    overflow: hidden;
                    transition: all 0.3s ease;

                    &:hover {
                      transform: translateY(-2px);
                      box-shadow: 0 15px 30px rgba(106, 83, 254, 0.163);
                    }
                  }
                `}
              >
                <PlainButton
                  text={
                    isCanceled
                      ? 'Get started'
                      : stripeCustomerId &&
                          subscription?.subscription?.plan === 'pro' &&
                          !vipUser
                        ? 'Manage my subscription'
                        : 'Start free trial'
                  }
                  animate
                  animationDelay={0.7}
                  type="submit"
                  width="100%"
                />
              </div>
            </form>
            <Separator.Horizontal spacing={1} />
            {!isCanceled && (
              <div
                className={css`
                  width: 90%;
                  animation: ${fadeInUp} 0.8s ease-out 2.2s both;
                  & button {
                    position: relative;
                    overflow: hidden;
                    transition: all 0.3s ease;

                    &:hover {
                      transform: translateY(-2px);
                      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
                    }
                  }
                `}
              >
                <LightButton
                  text="Continue without credit card"
                  animate
                  animationDelay={0.7}
                  onClick={handleSkip}
                  width="100%"
                />
              </div>
            )}
          </div>
          {/* End of Workspace */}
        </div>

        {/* Quote Section */}
        <div
          className={css`
            height: 100%;
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 34px;
            position: relative;
            animation: ${slideInFromRight} 1s ease-out 0.9s both;
            @media (max-width: 648px) {
              padding: 12px;
              margin-top: 32px;
            }
          `}
        >
          <div
            className={css`
              position: absolute;
              top: 0px;
              left: 0;
              width: 1px;
              height: 0;
              border-left: 1px solid #3a3a3a;
              animation: ${drawLine} 1s ease-out 1.2s forwards;
              @media (max-width: 1024px) {
                top: -60px;
                left: 0px;
                width: 0;
                height: 1px;
                border-left: none;
                border-top: 1px solid #3a3a3a;
                animation: ${drawLineHorizontal} 1s ease-out 1.2s forwards;
              }
            `}
          />
          <div
            className={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
              max-width: 642px;
              @media (max-width: 648px) {
                width: 100%;
              }
            `}
          >
            <div
              className={css`
                position: relative;
                width: 100%;
                margin-bottom: 16px;
                @media (max-width: 648px) {
                  margin-top: 32px;
                }
              `}
            >
              <div
                className={css`
                  position: absolute;
                  top: -60px;
                  left: 0px;
                  animation: ${bounceIn} 0.8s ease-out 1.4s both;
                `}
              >
                <QuoteIcon
                  fill="highlight/1"
                  style={{ width: '47px', height: '32px' }}
                  className={css`
                    animation: ${pulse} 2s ease-in-out infinite;
                    animation-delay: 2.5s;
                  `}
                />
              </div>
              <Text.H5
                style={{
                  fontSize: '32px',
                  lineHeight: '40px',
                  minHeight: '80px',
                }}
                className={css`
                  @media (max-width: 648px) {
                    font-size: 24px;
                    line-height: 32px;
                    min-height: 60px;
                  }
                  &::after {
                    content: '|';
                    animation: ${fadeIn} 0.7s ease-in-out infinite alternate;
                    margin-left: 2px;
                    display: ${displayedQuote.length === fullQuote.length
                      ? 'none'
                      : 'inline'};
                  }
                `}
              >
                {displayedQuote}
              </Text.H5>
            </div>
            <Text.Body
              color="highlight/1"
              className={css`
                animation: ${fadeInUp} 0.8s ease-out 2.5s both;
              `}
            >
              Jorge O.
              <br /> Founder
            </Text.Body>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

type StepItemProps = {
  title: string;
  subtitle: string;
  delay?: number;
  isFirst?: boolean;
  isLast?: boolean;
};
function StepItem({
  title,
  subtitle,
  delay = 0.2,
  isFirst,
  isLast,
}: StepItemProps) {
  // animation: ${sparkle} 2s ease-in-out ${delay + 0.5}s;

  return (
    <div
      className={css`
        width: 100%;
        display: flex;
        flex-direction: row;
        z-index: 1;
        opacity: 0;
        animation: ${slideInFromLeft} 0.8s ease-in-out ${delay}s forwards;
        position: relative;
        padding-left: 0;
        transition: all 0.3s ease;
      `}
    >
      <div
        className={css`
          width: 24px;
          height: 24px;
          display: flex;
          justify-content: center;
          align-items: center;
          margin-right: 21px;
          position: relative;
        `}
      >
        <StarIcon
          fill="primary"
          style={{ width: '16px', height: '16px' }}
          className={css`
            animation: ${bounceIn} 0.5s ease-out ${delay + 0.3}s both;
          `}
        />
      </div>
      <div
        className={css`
          & > * {
            transition: color 0.3s ease;
          }
          &:hover > * {
            color: #ffffff !important;
          }
        `}
      >
        <Text.Body
          color="primary"
          style={{ fontSize: '24px', marginBottom: '8px' }}
          className={css`
            background: linear-gradient(
              90deg,
              ${isFirst ? '#6A53FE' : isLast ? '#AAEF65' : '#ffffff'},
              #ffffff
            );
            -webkit-background-clip: text;
            background-clip: text;
          `}
        >
          {title}
        </Text.Body>
        <Text.BodySM color="seconday">{subtitle}</Text.BodySM>
      </div>
    </div>
  );
}

type PricingItemProps = {
  price: number;
};
function PricingItem({ price }: PricingItemProps) {
  const [itemsVisible, setItemsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setItemsVisible(true);
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={css`
        border-radius: 24px;
        border: 1px solid #29282f;
        background: #161617;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        padding: 32px;
        margin-top: 42px;
        margin-bottom: 18px;
        animation: ${fadeInUp} 0.8s ease-out 0.8s both;
        transition: all 0.3s ease;
        position: relative;
        overflow: hidden;
        cursor: pointer;

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(170, 239, 101, 0.05),
            transparent
          );
          transition: left 0.6s ease;
        }

        @media (max-width: 648px) {
          margin-bottom: 24px;
        }
      `}
    >
      <div
        className={css`
          width: 100%;
          display: grid;
          grid-template-columns: 35% 1fr;
          gap: 80px;
          @media (max-width: 648px) {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        `}
      >
        <div
          className={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            animation: ${bounceIn} 0.8s ease-out 1.2s both;
          `}
        >
          <Text.H2
            style={{ fontSize: '72px' }}
            color="highlight/1"
            className={css`
              background: linear-gradient(135deg, #aaef65, #6a53fe, #aaef65);
              background-size: 200% 200%;
              -webkit-background-clip: text;
              background-clip: text;
              animation: ${gradientShift} 3s ease-in-out infinite;
            `}
          >
            ${price}
            <br />
            <span
              style={{ fontSize: '21px', color: '#fff' }}
              className={css`
                animation: ${fadeIn} 1s ease-out 1.5s both;
              `}
            >
              / month
            </span>
          </Text.H2>
        </div>

        <div
          className={css`
            display: block;
            width: 100%;
          `}
        >
          {pricingTexts.map((text, index) => (
            <div
              key={index}
              className={css`
                width: 100%;
                padding: 12px 0;
                border-bottom: 1px solid #212121;
                border-bottom-width: ${index === pricingTexts.length - 1
                  ? '0'
                  : '1px'};
                opacity: ${itemsVisible ? '1' : '0'};
                transform: ${itemsVisible
                  ? 'translateX(0)'
                  : 'translateX(-20px)'};
                transition: all 0.4s ease-out;
                transition-delay: ${itemsVisible
                  ? `${0.1 * index + 1.2}s`
                  : '0s'};
                position: relative;
              `}
            >
              <Text.BodySM
                color="secondary"
                style={{
                  fontSize: '15px',
                  transition: 'color 0.3s ease',
                }}
                className={css`
                  &:hover {
                    color: #ffffff;
                  }
                `}
              >
                {text}
              </Text.BodySM>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
